import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Text,
  Flex,
  ModalCloseButton,
  Spinner,
  Heading,
  ModalBody,
  FormControl,
  useDisclosure,
  Button,
  ModalFooter,
} from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { InternalUser, userApi } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorDetails } from '../alert/ErrorDetails';
import { EmptyDataButton } from '../button/EmptyDataButton';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { TexasSelect } from '../form/TexasSelect';
import { Icons } from '../Icons';
import { SimpleNote } from '../SimpleNote';
import { SharedDisclosureProps } from '../types';
import { AddInternalTransportRequestModal } from './AddInternalTransportRequestModal';
import { request } from '@texas/utils/helpers/httpHelpers';
import { transportRequestsApi } from '@texas/api/endpoints/transportRequestApi';
import { TransportRequestItem } from './TransportRequestItem';
import { SamplesTable } from '../samples/SamplesTable';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onAdded: () => void;
}

export function AddSamplesToTransportInternalRequestModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex gap={2} align="center">
            <Text>{t('transportRequest.internal')}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onAdd={() => {
            props.onAdded();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onAdd,
}: {
  sampleIds: number[];
  onAdd: () => void;
}) {
  const {
    data: samples,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(samplesApi.fromIds);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  const [trId, setTrId] = useState<number | null>(null);
  const { t } = useTranslation();

  const {
    request: addSamplesRequest,
    loading: requestLoading,
    error: requestError,
  } = useApiRequest(transportRequestsApi.addSamplesToTransportRequest);

  const performRequest = () => {
    if (!trId) return;
    request(addSamplesRequest, [trId, sampleIds], () => {
      onAdd();
    });
  };

  return (
    <>
      {requestError && <ErrorDetails error={requestError} />}
      <ModalBody>
        <Flex gap={4} alignItems="center">
          <Flex flexDir="column" gap={2} w="60%">
            {error && <ErrorDetails error={error} />}
            {samplesLoading && <Spinner />}
            <SamplesTable
              samples={samples ?? []}
              maxH="600px"
              overflowY="auto"
            />
          </Flex>
          <Icons.ArrowRight opacity={0.2} boxSize={12} />
          <TransportsList setTrId={setTrId} selectedId={trId} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          isDisabled={trId === null || requestLoading}
          isLoading={requestLoading}
          onClick={performRequest}
        >
          {t('general.save')}
        </Button>
      </ModalFooter>
    </>
  );
}

function TransportsList({
  selectedId,
  setTrId,
}: {
  selectedId: number | null;
  setTrId: Dispatch<SetStateAction<number | null>>;
}) {
  const { t } = useTranslation();

  const {
    data: users,
    refetch: refetchUsers,
    loading: loadingUsers,
    error: loadingUsersError,
  } = useApiResource(userApi.getInternalUsersWithBranch);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [user, setUser] = useState<InternalUser | null>(null);

  const {
    data: upcoming,
    refetch: refetchUpcoming,
    loading: upcomingLoading,
    set: setUpcoming,
  } = useApiResource(transportRequestsApi.upcomingFromMeToInternal);

  const { request: removeReq, ...removeRes } = useApiRequest(
    transportRequestsApi.remove,
  );

  const performRemove = (id: number) =>
    request(removeReq, [id], () => {
      setTrId(null);
      if (!user) return;
      refetchUpcoming(user.id);
    });

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  useEffect(() => {
    if (!user) {
      setUpcoming([]);
      return;
    }
    refetchUpcoming(user.id);
  }, [refetchUpcoming, user, setUpcoming]);

  useEffect(() => {
    if (upcoming !== null && upcoming.length > 0) {
      setTrId(upcoming[0].id);
    }
  }, [setTrId, upcoming]);

  return (
    <>
      <Flex flexDir="column" gap={2} w="40%">
        <Heading fontSize="md">{t('transportRequest.select')}</Heading>
        <FormControl>
          <TexasFormLabel>
            {t('transportRequest.whoWillRecieve')}
          </TexasFormLabel>
          <TexasSelect
            isLoading={loadingUsers}
            onChange={(e) => {
              setTrId(null);
              setUser(users?.find((u) => u.id === e?.value) ?? null);
            }}
            options={users?.map((u) => ({ label: u.name, value: u.id }))}
          />
          <ErrorDetails error={loadingUsersError} />
        </FormControl>
        {user ? (
          <>
            {upcomingLoading || removeRes.loading ? (
              <Flex gap={2}>
                <Spinner />
                <Text>{t('general.loadingData')}</Text>
              </Flex>
            ) : (
              <>
                {upcoming &&
                  (upcoming.length == 0 ? (
                    <Text variant="sub" fontSize="sm" pb={4}>
                      {t('transportRequest.noPending', {
                        name: user.name,
                      })}
                    </Text>
                  ) : (
                    upcoming.length > 0 && (
                      <>
                        <Text variant="sub" fontSize="sm" pb={4}>
                          {t('transportRequest.showingFromYouTo', {
                            name: user.name,
                          })}
                        </Text>
                        {upcoming.map((x) => (
                          <TransportRequestItem
                            onClick={() => setTrId(x.id)}
                            onRemove={
                              x.samplesInTransport === 0
                                ? () => performRemove(x.id)
                                : undefined
                            }
                            isSelected={selectedId === x.id}
                            key={x.id}
                            transport={x}
                          />
                        ))}
                      </>
                    )
                  ))}
                <EmptyDataButton
                  label={t('transportRequest.createTicket')}
                  description={t('transportRequest.request')}
                  onClick={onOpen}
                />
              </>
            )}
          </>
        ) : (
          <SimpleNote>{t('transportRequest.selectUser')}</SimpleNote>
        )}
      </Flex>
      <AddInternalTransportRequestModal
        isOpen={isOpen}
        onClose={onClose}
        onCreate={(id) => {
          if (!user) return;
          setTrId(id);
          refetchUpcoming(user.id);
        }}
        toBranchId={user?.branchId}
        toUserId={user?.id}
      />
    </>
  );
}
